import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, model, output, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

export type DatePickerView = 'day' | 'month' | 'year';

/** Période couverte par la vue affichée, bornes incluses (`YYYY-MM-DD`), et taille des cases de cette vue. */
export interface DatePickerRange {
  from: string;
  to: string;
  granularity: DatePickerView;
}

/** Une case de la vue jour : null pour les jours hors du mois affiché. */
interface DayCell {
  iso: string;
  day: number;
  isToday: boolean;
  isMarked: boolean;
  isSelected: boolean;
}

interface PeriodCell {
  value: number;
  label: string;
  isMarked: boolean;
  isSelected: boolean;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEARS_PER_PAGE = 12;
/** Une page d'années commence à 2019, puis tous les douze ans (2019-2030, 2031-2042…). */
const YEAR_PAGE_ORIGIN = 2019;

/**
 * Sélecteur de date : un déclencheur affichant le mois de la valeur, qui ouvre un
 * calendrier. Les trois cases du bas (jour / mois / année) montrent la saisie en
 * cours et basculent entre les vues jour, mois et année. Le choix n'est appliqué
 * qu'au clic sur « Valider » ; sans jour choisi, le 1er du mois est retenu.
 *
 * `markedDates` pose un point sous les jours concernés, et sous leur mois et leur
 * année dans les vues correspondantes. Il accepte aussi des mois (`YYYY-MM`) ou des
 * années (`YYYY`) seuls, pour les vues larges. `visibleRangeChange` signale la
 * période affichée pour que le parent les charge.
 */
@Component({
  selector: 'app-date-picker',
  standalone: true,
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  imports: [OverlayModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent {
  /** Date choisie (`YYYY-MM-DD`, jour local) ; null = aucune. */
  readonly value         = model<string | null>(null);
  /** Jours (`YYYY-MM-DD`), mois (`YYYY-MM`) ou années (`YYYY`) signalés par un point. */
  readonly markedDates   = input<readonly string[]>([]);
  /** Format du libellé du déclencheur ; par défaut « juin 2026 ». */
  readonly displayFormat = input<Intl.DateTimeFormatOptions>({ month: 'long', year: 'numeric' });

  /** Période affichée, à l'ouverture puis à chaque changement de page ou de vue. */
  readonly visibleRangeChange = output<DatePickerRange>();

  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
  ];

  private readonly trigger = viewChild.required<ElementRef<HTMLElement>>('trigger');

  private readonly translate = inject(TranslateService);
  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );
  private readonly locale = computed<string | undefined>(() => this.lang() || undefined);

  protected readonly isOpen = signal(false);
  protected readonly view   = signal<DatePickerView>('day');
  /** Premier jour du mois affiché ; son année pilote les vues mois et année. */
  protected readonly cursor = signal<Date>(firstOfMonth(new Date()));

  // Saisie en cours, appliquée par « Valider ». Mois de 0 à 11.
  protected readonly draftDay   = signal<number | null>(null);
  protected readonly draftMonth = signal<number | null>(null);
  protected readonly draftYear  = signal<number | null>(null);

  protected readonly canConfirm = computed<boolean>(() => this.draftMonth() !== null && this.draftYear() !== null);

  private readonly marked = computed<Set<string>>(() => {
    // Un jour marque aussi son mois et son année.
    const keys = new Set<string>();
    for (const value of this.markedDates()) {
      keys.add(value);
      keys.add(value.slice(0, 7));
      keys.add(value.slice(0, 4));
    }
    return keys;
  });

  protected readonly triggerLabel = computed<string>(() => {
    const date = fromIso(this.value());
    return date ? date.toLocaleDateString(this.locale(), this.displayFormat()) : this.translate.instant('DATE_PICKER.PICK_DATE');
  });

  protected readonly title = computed<string>(() => {
    const cursor = this.cursor();
    switch (this.view()) {
      case 'day':   return cursor.toLocaleDateString(this.locale(), { month: 'long', year: 'numeric' });
      case 'month': return String(cursor.getFullYear());
      case 'year': {
        const start = yearPageStart(cursor.getFullYear());
        return `${start}-${start + YEARS_PER_PAGE - 1}`;
      }
    }
  });

  /** Initiales des jours, du lundi au dimanche. */
  protected readonly weekdays = computed<string[]>(() =>
    Array.from({ length: 7 }, (_, i) =>
      new Date(2024, 0, 1 + i).toLocaleDateString(this.locale(), { weekday: 'narrow' }), // 1er janvier 2024 : un lundi
    ),
  );

  protected readonly monthNames = computed<string[]>(() =>
    Array.from({ length: 12 }, (_, i) => new Date(2024, i, 1).toLocaleDateString(this.locale(), { month: 'short' })),
  );

  protected readonly weeks = computed<(DayCell | null)[][]>(() => {
    const first = this.cursor();
    const year = first.getFullYear();
    const month = first.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leading = (first.getDay() + 6) % 7; // cases vides avant le 1er (lundi = 0)
    const today = toIso(new Date());
    const marked = this.marked();
    const selected = this.draftYear() === year && this.draftMonth() === month ? this.draftDay() : null;

    const cells: (DayCell | null)[] = Array.from({ length: leading }, () => null);
    for (let day = 1; day <= daysInMonth; day++) {
      const iso = toIso(new Date(year, month, day));
      cells.push({ iso, day, isToday: iso === today, isMarked: marked.has(iso), isSelected: day === selected });
    }
    while (cells.length % 7 !== 0) { cells.push(null); }

    const weeks: (DayCell | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) { weeks.push(cells.slice(i, i + 7)); }
    return weeks;
  });

  protected readonly months = computed<PeriodCell[]>(() => {
    const year = this.cursor().getFullYear();
    const marked = this.marked();
    return this.monthNames().map((label, month) => ({
      value: month,
      label,
      isMarked: marked.has(`${year}-${pad(month + 1)}`),
      isSelected: this.draftYear() === year && this.draftMonth() === month,
    }));
  });

  protected readonly years = computed<PeriodCell[]>(() => {
    const start = yearPageStart(this.cursor().getFullYear());
    const marked = this.marked();
    return Array.from({ length: YEARS_PER_PAGE }, (_, i) => {
      const year = start + i;
      return { value: year, label: String(year), isMarked: marked.has(String(year)), isSelected: this.draftYear() === year };
    });
  });

  protected readonly dayLabel   = computed<string | null>(() => this.draftDay() === null ? null : String(this.draftDay()));
  protected readonly monthLabel = computed<string | null>(() => {
    const month = this.draftMonth();
    return month === null ? null : this.monthNames()[month];
  });

  private lastRangeKey = '';

  protected toggle(): void {
    if (this.isOpen()) { this.close(); } else { this.open(); }
  }

  protected open(): void {
    const current = fromIso(this.value());
    this.draftDay.set(current?.getDate() ?? null);
    this.draftMonth.set(current?.getMonth() ?? null);
    this.draftYear.set(current?.getFullYear() ?? null);
    this.cursor.set(firstOfMonth(current ?? new Date()));
    this.view.set('day');
    this.lastRangeKey = '';
    this.isOpen.set(true);
    this.emitRange();
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  protected onOutsideClick(event: MouseEvent): void {
    // Le clic sur le déclencheur est géré par toggle() : ne pas fermer pour rouvrir aussitôt.
    if (this.trigger().nativeElement.contains(event.target as Node)) { return; }
    this.close();
  }

  protected setView(view: DatePickerView): void {
    this.view.set(view);
    this.emitRange();
  }

  /** Page précédente ou suivante : un mois, une année ou douze ans selon la vue. */
  protected shift(direction: -1 | 1): void {
    const cursor = this.cursor();
    const months = { day: 1, month: 12, year: 12 * YEARS_PER_PAGE }[this.view()];
    this.cursor.set(new Date(cursor.getFullYear(), cursor.getMonth() + direction * months, 1));
    this.emitRange();
  }

  protected selectDay(cell: DayCell): void {
    const cursor = this.cursor();
    this.draftDay.set(cell.day);
    this.draftMonth.set(cursor.getMonth());
    this.draftYear.set(cursor.getFullYear());
  }

  protected selectMonth(month: number): void {
    const year = this.cursor().getFullYear();
    this.draftMonth.set(month);
    this.draftYear.set(year);
    this.clampDay();
    this.cursor.set(new Date(year, month, 1));
  }

  protected selectYear(year: number): void {
    this.draftYear.set(year);
    this.clampDay();
    this.cursor.set(new Date(year, this.cursor().getMonth(), 1));
  }

  protected confirm(): void {
    const month = this.draftMonth();
    const year = this.draftYear();
    if (month === null || year === null) { return; }
    this.value.set(toIso(new Date(year, month, this.draftDay() ?? 1)));
    this.close();
    this.trigger().nativeElement.focus();
  }

  /** Ramène le jour choisi dans le mois saisi (31 → 30 en juin, 29 → 28 en février non bissextile). */
  private clampDay(): void {
    const day = this.draftDay();
    const month = this.draftMonth();
    const year = this.draftYear();
    if (day === null || month === null || year === null) { return; }
    this.draftDay.set(Math.min(day, new Date(year, month + 1, 0).getDate()));
  }

  private emitRange(): void {
    const cursor = this.cursor();
    const year = cursor.getFullYear();
    let from: Date;
    let to: Date;
    switch (this.view()) {
      case 'day':
        from = cursor;
        to = new Date(year, cursor.getMonth() + 1, 0);
        break;
      case 'month':
        from = new Date(year, 0, 1);
        to = new Date(year, 11, 31);
        break;
      case 'year': {
        const start = yearPageStart(year);
        from = new Date(start, 0, 1);
        to = new Date(start + YEARS_PER_PAGE - 1, 11, 31);
        break;
      }
    }
    const range: DatePickerRange = { from: toIso(from), to: toIso(to), granularity: this.view() };
    const key = `${range.from}_${range.to}_${range.granularity}`;
    if (key === this.lastRangeKey) { return; }
    this.lastRangeKey = key;
    this.visibleRangeChange.emit(range);
  }
}

function yearPageStart(year: number): number {
  const offset = ((year - YEAR_PAGE_ORIGIN) % YEARS_PER_PAGE + YEARS_PER_PAGE) % YEARS_PER_PAGE;
  return year - offset;
}

function firstOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toIso(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromIso(value: string | null | undefined): Date | null {
  const match = ISO_DATE.exec(value ?? '');
  if (!match) { return null; }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}
